import { z } from 'zod';

/**
 * Game rules (R8, data's shape). Stored on games.rules as JSON. Points are never stored on
 * events: shotValue(rules, shotClass) scores them on replay, so a wrong rules pick can be
 * fixed after the game. No shot clock and no overtime in v1.
 */
export const ScoringZ = z.enum(['ones_twos', 'twos_threes']);
export const FreeThrowsZ = z.enum(['none', 'and_one', 'bonus']);
export const PossessionZ = z.enum(['make_it_take_it', 'loser_ball', 'alternate']);

export const RulesZ = z
  .object({
    v: z.literal(1),
    /** Inside / three = 1 / 2 or 2 / 3. */
    scoring: ScoringZ,
    freeThrows: FreeThrowsZ,
    /** Any integer 1..150. Ignored when `clock` is set (timed games end by an end event). */
    target: z.number().int().min(1).max(150),
    winBy: z.union([z.literal(1), z.literal(2)]),
    /** Hard cap: first to reach it with any lead wins. null = none. Must be >= target. */
    hardCap: z.number().int().min(1).max(200).nullable(),
    possession: PossessionZ,
    clock: z
      .object({
        periods: z.number().int().min(1).max(4),
        minutes: z.number().min(1).max(30),
      })
      .nullable(),
  })
  .refine((r) => r.hardCap === null || r.hardCap >= r.target, {
    message: 'hardCap must be at least the target',
    path: ['hardCap'],
  });
export type Rules = z.infer<typeof RulesZ>;

/** Starting presets for the setup screen. Games may change any field. */
export const RULE_PRESETS = {
  quick11: {
    v: 1,
    scoring: 'ones_twos',
    freeThrows: 'none',
    target: 11,
    winBy: 2,
    hardCap: 15,
    possession: 'make_it_take_it',
    clock: null,
  },
  to21: {
    v: 1,
    scoring: 'twos_threes',
    freeThrows: 'none',
    target: 21,
    winBy: 2,
    hardCap: 25,
    possession: 'loser_ball',
    clock: null,
  },
  iso3: {
    v: 1,
    scoring: 'ones_twos',
    freeThrows: 'none',
    target: 3,
    winBy: 1,
    hardCap: null,
    possession: 'make_it_take_it',
    clock: null,
  },
} as const satisfies Record<string, Rules>;
